import { FileTextIcon, BarChart3Icon, Shield, Lightbulb, BookOpenIcon, ArrowRightIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

const ResearchShowcase = () => {
  const researchPapers = [
    {
      title: "StaffAbility Model Overview",
      description: "Comprehensive white paper on breaking career barriers",
      icon: FileTextIcon,
      color: "primary",
      badge: "Updated"
    },
    {
      title: "Business Case for Hiring",
      description: "ROI analysis and business benefits",
      icon: BarChart3Icon,
      color: "purple",
      badge: null
    },
    {
      title: "Impact & Opportunity Report",
      description: "Data-driven insights and measurable outcomes",
      icon: BarChart3Icon,
      color: "blue",
      badge: "Featured"
    },
    {
      title: "Youth Crime Prevention",
      description: "Evidence-based intervention strategies",
      icon: Shield,
      color: "green",
      badge: "New"
    },
    {
      title: "Vocational Training Programs",
      description: "Skills development and best practices",
      icon: Lightbulb,
      color: "indigo",
      badge: null
    },
    {
      title: "Hybrid Nonprofit Models",
      description: "Innovative sustainable impact approaches",
      icon: BookOpenIcon,
      color: "teal",
      badge: null
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      primary: "bg-primary-50 text-primary-700 border-primary-200",
      purple: "bg-purple-50 text-purple-700 border-purple-200",
      blue: "bg-blue-50 text-blue-700 border-blue-200",
      green: "bg-green-50 text-green-700 border-green-200",
      indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
      teal: "bg-teal-50 text-teal-700 border-teal-200"
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.primary;
  };

  const getBadgeClasses = (color: string) => {
    const badgeMap = {
      primary: "bg-primary-100 text-primary-700",
      purple: "bg-purple-100 text-purple-700",
      blue: "bg-blue-100 text-blue-700",
      green: "bg-green-100 text-green-700",
      indigo: "bg-indigo-100 text-indigo-700",
      teal: "bg-teal-100 text-teal-700"
    };
    return badgeMap[color as keyof typeof badgeMap] || badgeMap.primary;
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-4">
            Research & Case Studies
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Evidence-based insights driving justice reform through comprehensive research, 
            data analysis, and proven methodologies for second-chance employment success.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-12">
          {researchPapers.map((paper, index) => {
            const IconComponent = paper.icon;
            return (
              <Link key={index} href="/research">
                <div
                  className={`h-full p-6 rounded-xl border-2 transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer flex flex-col ${getColorClasses(paper.color)}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-lg ${getBadgeClasses(paper.color)}`}>
                      <IconComponent className="h-6 w-6" />
                    </div>
                    {paper.badge && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBadgeClasses(paper.color)}`}>
                        {paper.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {paper.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 flex-grow">
                    {paper.description}
                  </p>
                  <div className="flex items-center justify-center text-sm font-medium text-gray-700 mt-auto">
                    <span>Read Research</span>
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Action Buttons - Three button grid layout */}
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link href="/research">
              <Button 
                variant="outline" 
                size="lg" 
                className="w-full px-8"
              >
                <BookOpenIcon className="h-5 w-5 mr-2" />
                View All Research Papers
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full px-8"
              onClick={() => window.open('https://drive.google.com/file/d/1xk1YrIkxHSgEBB2bkmiBTFXtUOi8BfG-/view?usp=sharing', '_blank')}
            >
              <Lightbulb className="h-5 w-5 mr-2" />
              Program Implementation Guide
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="w-full px-8"
              onClick={() => {
                const contactSection = document.getElementById('contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  window.location.href = '/contact';
                }
              }}
            >
              <FileTextIcon className="h-5 w-5 mr-2" />
              Request Custom Research
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResearchShowcase;